<template>
    <template v-for="paramKey in displayOrder" :key="paramKey">
        <template v-if="changedParams[paramKey]">
            <div :class="paramKey" v-if="paramKey === 'extra' && hasActiveExtraValues">
                <div class="h6 name">{{$t('fields.dataset_settings.label')}}</div>
                <ul v-if="typeof changedParams[paramKey] === 'object'">
                    <li v-for="(val, key) in getFilteredExtraValues(changedParams[paramKey])" :key="key">
                        <div>{{$t(`fields.dataset_settings.options.${key}`)}}</div>
                    </li>
                </ul>
            </div>

            <div :class="[paramKey, changedParams[paramKey].custom ? 'spec-text' : 'spec-inex' ]" v-else-if="paramKey === 'text'">
                <div class="h6 name">{{$t('fields.text_search.label')}}</div>

                <ul v-if="changedParams[paramKey].required?.length > 0">
                    <li v-for="(item, index) in changedParams[paramKey].required" class="required" :key="'required-'+index">
                        <div>{{item.text}}</div>
                        <button @click="removeListItem(paramKey, 'required', item)" v-if="canModify"></button>
                    </li>
                </ul>

                <ul v-if="changedParams[paramKey].includes?.length > 0" :class="[changedParams[paramKey].includes.length > 1 ? 'show-or' : '']">
                    <li v-for="(item, index) in changedParams[paramKey].includes" :class="changedParams[paramKey].includes.length > 1 ? 'include' : 'required'" :key="'include-'+index">
                        <div>{{item.text}}</div>
                        <button @click="removeListItem(paramKey, 'includes', item)" v-if="canModify"></button>
                    </li>
                </ul>
                
                <ul v-if="changedParams[paramKey].excludes?.length > 0">
                    <li v-for="(item, index) in changedParams[paramKey].excludes" class="exclude" :key="'exclude-'+index">
                        <div>{{item.text}}</div>
                        <button @click="removeListItem(paramKey, 'excludes', item)" v-if="canModify"></button>
                    </li>
                </ul>

                <div v-if="changedParams[paramKey].custom !== ''" class="custom-text">
                    <div v-html="formatCustomQuery(changedParams[paramKey].custom)"></div>
                    <button @click="setDefault('text')" v-if="canModify"></button>
                </div>
            </div>

            <div class="spec-inex" v-else-if="['sbi', 'apps', 'relations'].includes(paramKey)">
                <div class="h6 name">{{$te(`fields.${paramKey}_alt.label`) ? $t(`fields.${paramKey}_alt.label`) : $t(`fields.${paramKey}.label`)}}</div>
                <ul v-if="changedParams[paramKey].required?.length > 0">
                    <li v-for="(item, index) in changedParams[paramKey].required" class="required" :class="item.icon ? 'has-icon' : ''" :key="'required-'+index">
                        <div v-if="paramKey === 'sbi'">{{formatSbiCode(item.value) + ' - ' + item.text}}</div>
                        <div v-else-if="paramKey === 'relations'"><img v-if="item.icon" :src="item.icon"/>{{item.value}}</div>
                        <div v-else-if="item.icon"><img :src="item.icon"/>{{item.text}}</div>
                        <div v-else>{{item.text}}</div>
                        <button @click="removeListItem(paramKey, 'required', item)" v-if="canModify"></button>
                    </li>
                </ul>

                <ul v-if="changedParams[paramKey].includes?.length > 0" :class="[changedParams[paramKey].includes.length > 1 ? 'show-or' : '']">
                    <li v-for="(item, index) in changedParams[paramKey].includes" :class="[changedParams[paramKey].includes.length > 1 ? 'include' : 'required', item.icon ? 'has-icon' : '']" :key="'include-'+index">
                        <div v-if="paramKey === 'sbi'">{{formatSbiCode(item.value) + ' - ' + item.text}}</div>
                        <div v-else-if="paramKey === 'relations'"><img v-if="item.icon" :src="item.icon"/>{{item.value}}</div>
                        <div v-else-if="item.icon"><img :src="item.icon"/>{{item.text}}</div>
                        <div v-else>{{item.text}}</div>
                        <button @click="removeListItem(paramKey, 'includes', item)" v-if="canModify"></button>
                    </li>
                </ul>

                <ul v-if="changedParams[paramKey].excludes?.length > 0">
                    <li v-for="(item, index) in changedParams[paramKey].excludes" class="exclude" :class="item.icon ? 'has-icon' : ''" :key="'exclude-'+index">
                        <div v-if="paramKey === 'sbi'">{{formatSbiCode(item.value) + ' - ' + item.text}}</div>
                        <div v-else-if="paramKey === 'relations'"><img v-if="item.icon" :src="item.icon"/>{{item.value}}</div>
                        <div v-else-if="item.icon"><img :src="item.icon"/>{{item.text}}</div>
                        <div v-else>{{item.text}}</div>
                        <button @click="removeListItem(paramKey, 'excludes', item)" v-if="canModify"></button>
                    </li>
                </ul>
            </div>

            <div :class="[paramKey, changedParams[paramKey].city ? 'spec-text' : 'spec-list']" v-else-if="paramKey === 'location'">
                <div class="h6 name">{{$t('fields.location.label')}}</div>

                <div v-if="changedParams[paramKey].city">
                    <div>
                        {{changedParams[paramKey].city}}
                        <template v-if="changedParams[paramKey].radius">
                            (+{{changedParams[paramKey].radius}} {{$t('fields.radius_alt.option_suffix')}})
                        </template>
                    </div>
                    <button @click="setDefault('location')" v-if="canModify"></button>
                </div>

                <ul v-else-if="changedParams[paramKey].province?.length > 0" :class="[changedParams[paramKey].province.length > 1 ? 'show-or' : '']">
                    <li v-for="(item, index) in changedParams[paramKey].province" :class="[changedParams[paramKey].province.length > 1 ? 'include' : 'required', getProvinceItem(item).icon ? 'has-icon' : '']" :key="'province-'+index">
                        <div><div v-if="getProvinceItem(item).icon" :class="`icon-${getProvinceItem(item).icon}-pos`"></div>{{getProvinceItem(item).text}}</div>
                        <button @click="removeListItem('location', 'province', item)" v-if="canModify"></button>
                    </li>
                </ul>

                <div v-else>
                    <div>
                        {{$t('common.netherlands')}}
                    </div>
                </div>
            </div>

            <div class="spec-range" :class="paramKey" v-else-if="paramKey === 'founded'">
                <div class="h6 name">{{$t('fields.founded_alt.label') }}</div>
                <div>
                    <div v-if="changedParams[paramKey].min && changedParams[paramKey].max">{{$t(`fields.founded_alt.results.range`, { min: changedParams[paramKey].min, max: changedParams[paramKey].max})}}</div>
                    <div v-else-if="changedParams[paramKey].min">{{$t(`fields.founded_alt.results.min`, { min: changedParams[paramKey].min })}}</div>
                    <div v-else-if="changedParams[paramKey].max">{{$t(`fields.founded_alt.results.max`, { max: changedParams[paramKey].max })}}</div>
                    <button @click="resetRangeValue('founded')" v-if="canModify"></button>
                </div>
            </div>

            <template v-else-if="paramKey === 'company_size'">
                <div class="spec-range employees" v-if="hasRangeValues(changedParams[paramKey].employees)">
                    <div class="h6 name">{{$t('fields.employees_alt.label') }}</div>
                    <div>
                        <div v-if="changedParams[paramKey].employees.min && changedParams[paramKey].employees.max">{{$t(`fields.employees_alt.results.range`, { min: formatNumber(changedParams[paramKey].employees.min), max: formatNumber(changedParams[paramKey].employees.max)})}}</div>
                        <div v-else-if="changedParams[paramKey].employees.min">{{$t(`fields.employees_alt.results.min`, { min: formatNumber(changedParams[paramKey].employees.min) })}}</div>
                        <div v-else-if="changedParams[paramKey].employees.max">{{$t(`fields.employees_alt.results.max`, { max: formatNumber(changedParams[paramKey].employees.max)})}}</div>
                        <button @click="resetNestedRangeValue('company_size', 'employees')" v-if="canModify"></button>
                    </div>
                </div>

                <div class="spec-range revenue" v-if="hasRangeValues(changedParams[paramKey].revenue)">
                    <div class="h6 name">{{$t('fields.revenue_alt.label') }}</div>
                    <div>
                        <div v-if="changedParams[paramKey].revenue.min && changedParams[paramKey].revenue.max">{{$t(`fields.revenue_alt.results.range`, { min: formatNumber(changedParams[paramKey].revenue.min), max: formatNumber(changedParams[paramKey].revenue.max)})}}</div>
                        <div v-else-if="changedParams[paramKey].revenue.min">{{$t(`fields.revenue_alt.results.min`, { min: formatNumber(changedParams[paramKey].revenue.min) })}}</div>
                        <div v-else-if="changedParams[paramKey].revenue.max">{{$t(`fields.revenue_alt.results.max`, { max: formatNumber(changedParams[paramKey].revenue.max)})}}</div>
                        <button @click="resetNestedRangeValue('company_size', 'revenue')" v-if="canModify"></button>
                    </div>
                </div>
            </template>

            <div class="spec-list" :class="paramKey" v-else-if="paramKey === 'orgtype'">
                <div class="h6 name">{{$t(`fields.orgtype_alt.label`) }}</div>
                <ul v-if="changedParams[paramKey]?.length > 0" :class="[changedParams[paramKey].length > 1 ? 'show-or' : '']">
                    <li v-for="(item, index) in changedParams[paramKey]" :class="changedParams[paramKey].length > 1 ? 'include' : 'required'" :key="'item-'+index">
                        <div>{{getOrgtypeName(item)}}</div>
                        <button @click="removeListItem(paramKey, null, item)" v-if="canModify"></button>
                    </li>
                </ul>
            </div>

            <div class="spec-list" :class="paramKey" v-else-if="paramKey === 'socials'">
                <div class="h6 name">{{$t(`fields.social_exists_alt.label`) }}</div>
                <ul v-if="changedParams[paramKey]?.length > 0" :class="[changedParams[paramKey].length > 1 ? 'show-or' : '']">
                    <li v-for="(item, index) in changedParams[paramKey]" :class="[getSocialItem(item).icon ? 'has-icon' : '', changedParams[paramKey].length > 1 ? 'include' : 'required']" :key="'item-'+index">
                        <div><div v-if="getSocialItem(item).icon" :class="`icon-${getSocialItem(item).icon}-pos`"></div>{{getSocialItem(item).text}}</div>
                        <button @click="removeListItem(paramKey, null, item)" v-if="canModify"></button>
                    </li>
                </ul>
            </div>

            <div :class="paramKey" v-else-if="paramKey === 'bag'">
                <div class="h6 name">{{$t('fields.bag.label') }}</div>
                
                <ul v-if="changedParams[paramKey]?.gebruiksdoel.length > 0" :class="[changedParams[paramKey].gebruiksdoel.length > 1 ? 'show-or' : '']">
                    <li v-for="(item, index) in changedParams[paramKey].gebruiksdoel" :class="changedParams[paramKey].gebruiksdoel.length > 1 ? 'include' : 'required'" :key="'gebruiksdoel-'+index">
                        <div>{{$t(`fields.bag_gebruiksdoel.option_${item}`)}}</div>
                        <button @click="removeListItem('bag', 'gebruiksdoel', item)" v-if="canModify"></button>
                    </li>
                </ul>
            
                <ul v-if="changedParams[paramKey]?.energielabel.length > 0" :class="[changedParams[paramKey].energielabel.length > 1 ? 'show-or' : '']">
                    <li v-for="(item, index) in changedParams[paramKey].energielabel" :class="changedParams[paramKey].energielabel.length > 1 ? 'include' : 'required'" :key="'energielabel-'+index">
                        <div>{{$t('fields.bag_energielabel.label')}} {{item}}</div>
                        <button @click="removeListItem('bag', 'energielabel', item)" v-if="canModify"></button>
                    </li>
                </ul>
            
                <div v-if="hasRangeValues(changedParams[paramKey].bouwjaar)">
                    <div v-if="changedParams[paramKey].bouwjaar.min && changedParams[paramKey].bouwjaar.max">{{$t(`fields.bag_bouwjaar.results.range`, { min: formatNumber(changedParams[paramKey].bouwjaar.min), max: formatNumber(changedParams[paramKey].bouwjaar.max)})}}</div>
                    <div v-else-if="changedParams[paramKey].bouwjaar.min">{{$t(`fields.bag_bouwjaar.results.min`, { min: formatNumber(changedParams[paramKey].bouwjaar.min) })}}</div>
                    <div v-else-if="changedParams[paramKey].bouwjaar.max">{{$t(`fields.bag_bouwjaar.results.max`, { max: formatNumber(changedParams[paramKey].bouwjaar.max)})}}</div>
                    <button @click="resetNestedRangeValue('bag', 'bouwjaar')" v-if="canModify"></button>
                </div>
            
                <div v-if="hasRangeValues(changedParams[paramKey].oppervlakte)">
                    <div v-if="changedParams[paramKey].oppervlakte.min && changedParams[paramKey].oppervlakte.max">{{$t(`fields.bag_oppervlakte.results.range`, { min: formatNumber(changedParams[paramKey].oppervlakte.min), max: formatNumber(changedParams[paramKey].oppervlakte.max)})}}</div>
                    <div v-else-if="changedParams[paramKey].oppervlakte.min">{{$t(`fields.bag_oppervlakte.results.min`, { min: formatNumber(changedParams[paramKey].oppervlakte.min) })}}</div>
                    <div v-else-if="changedParams[paramKey].oppervlakte.max">{{$t(`fields.bag_oppervlakte.results.max`, { max: formatNumber(changedParams[paramKey].oppervlakte.max)})}}</div>
                    <button @click="resetNestedRangeValue('bag', 'oppervlakte')" v-if="canModify"></button>
                </div>
            
                <div v-if="hasRangeValues(changedParams[paramKey].grondoppervlakte)">
                    <div v-if="changedParams[paramKey].grondoppervlakte.min && changedParams[paramKey].grondoppervlakte.max">{{$t(`fields.bag_grondoppervlakte.results.range`, { min: formatNumber(changedParams[paramKey].grondoppervlakte.min), max: formatNumber(changedParams[paramKey].grondoppervlakte.max)})}}</div>
                    <div v-else-if="changedParams[paramKey].grondoppervlakte.min">{{$t(`fields.bag_grondoppervlakte.results.min`, { min: formatNumber(changedParams[paramKey].grondoppervlakte.min) })}}</div>
                    <div v-else-if="changedParams[paramKey].grondoppervlakte.max">{{$t(`fields.bag_grondoppervlakte.results.max`, { max: formatNumber(changedParams[paramKey].grondoppervlakte.max)})}}</div>
                    <button @click="resetNestedRangeValue('bag', 'grondoppervlakte')" v-if="canModify"></button>
                </div>
            </div>
        </template>
    </template>
    <div class="legend">
        <div class="name">{{$t('common.legend') }}</div>
        <ul>
            <li class="custom-text required">
                <div>{{$t('common.required_long') }}</div>
            </li>
            <li class="custom-text include">
                <div>{{$t('common.include_long') }}</div>
            </li>
            <li class="custom-text exclude">
                <div>{{$t('common.exclude_long') }}</div>
            </li>
        </ul>
    </div>
</template>

<script>
    import { mapActions, mapState } from 'pinia';
    import { useDatasetStore } from '@/stores/dataset';
    import { formatNumber, formatSbiCode, formatCustomQuery } from '@/utils/formatting';
    import { orgtypeMap } from '@/utils/maps/orgtypeMap';
    import { socialMap } from '@/utils/maps/socialMap';
    import { provinceMap } from '@/utils/maps/provinceMap';

    export default {
        name: 'DatasetSelection',
        props: {
            canModify: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                displayOrder: [
                    'extra',
                    'text',
                    'sbi',
                    'company_size',
                    'location',
                    'founded',
                    'orgtype',
                    'socials', 
                    'apps',
                    'relations',
                    'bag'
                ]
            };
        },
        computed: {
            ...mapState(useDatasetStore, ['dataset', 'hasDefaultDataset', 'changedParams']),
            datasetStore() {
                return useDatasetStore();
            },
            hasActiveExtraValues() {
                if (!this.changedParams.extra || typeof this.changedParams.extra !== 'object') {
                    return false;
                }
                return Object.values(this.changedParams.extra).some(val => val === true);
            }
        },
        methods: {
            ...mapActions(useDatasetStore, ['updateDataset']),
            formatNumber,
            formatSbiCode,
            formatCustomQuery,
            setDefault(key) {
                if(this.datasetStore.defaultDataset[key] !== undefined) {
                    this.dataset[key] = JSON.parse(JSON.stringify(this.datasetStore.defaultDataset[key]));
                    this.updateDataset();
                }
            },
            getFilteredExtraValues(extraValues) {
                if (!extraValues || typeof extraValues !== 'object') {
                    return {};
                }
                
                return Object.entries(extraValues)
                    .filter(([, val]) => val === true)
                    .reduce((acc, [key, val]) => {
                        acc[key] = val;
                        return acc;
                    }, {});
            },
            removeListItem(section, type, item) {
                if (this.dataset[section]) {
                    if (type) {
                        const index = this.dataset[section][type].indexOf(item);
                        if (index !== -1) {
                            this.dataset[section][type].splice(index, 1);
                            this.updateDataset();
                        }
                    } else {
                        const index = this.dataset[section].indexOf(item);
                        if (index !== -1) {
                            this.dataset[section].splice(index, 1);
                            this.updateDataset();
                        }
                    }
                }
            },
            resetRangeValue(section) {
                if (this.dataset[section]) {
                    this.dataset[section].min = null;
                    this.dataset[section].max = null;
                    this.updateDataset();
                }
            },
            resetNestedRangeValue(section, subSection) {
                if (this.dataset[section] && this.dataset[section][subSection]) {
                    this.dataset[section][subSection].min = null;
                    this.dataset[section][subSection].max = null;
                    this.updateDataset();
                }
            },
            hasRangeValues(range) {
                return range && (range.min !== null || range.max !== null);
            },
            getOrgtypeName(value) {
                const orgtype = orgtypeMap.find(item => item.value === value);
                return orgtype ? orgtype.name : value;
            },
            getSocialItem(value) {
                const social = socialMap.find(item => item.value === value);
                return social ? {
                    text: social.text,
                    icon: social.icon || false
                } : value;
            },
            getProvinceItem(value) {
                const province = provinceMap.find(item => item.value === value);
                return province ? {
                    text: province.text,
                    icon: province.icon || false
                } : value;
            }
        }
    }
</script>